import type { NodeId, PipelineStage, SearchScope, TagId } from '../../lib/types';
import type { SearchDocKind } from '../../lib/location';
import type { SliceCreator } from '../state';

/**
 * Everything that narrows what the map shows: the search box, the tag filter and the
 * AI-documentation status filter.
 *
 * TWO FIELDS FOR ONE SEARCH, and that is not redundancy. `searchText` is the raw string,
 * character for character as typed, and it is what the controlled input is bound to;
 * `searchQuery` is `trim().toLowerCase()` and is what every filter matches against. Binding
 * the box to the normalised form would rewrite what somebody typed — "Ab" would appear as
 * "ab" — and move their caret on every keystroke. The vanilla script grew the same pair when
 * the search box became the port's first controlled input, which is where this shape comes
 * from rather than being invented here.
 *
 * `forceOpen` is DERIVED and never persisted: `lib/search.ts`'s `computeForceOpen()` rebuilds
 * it from whatever filter state is current, so that a hit inside a collapsed branch is
 * revealed without the branch being permanently expanded. It is stored rather than recomputed
 * per read because `isHidden()` asks for it once per node per render.
 */
export interface FiltersSlice {
  /** The selected filter tags — several at once since plan 2.3 (OR semantics; empty = none). */
  activeTags: ReadonlySet<TagId>;
  /** Raw, exactly as typed. Bound to the input; nothing matches against it. */
  searchText: string;
  /** Trimmed and lowercased. Every filter matches against THIS; nothing renders it. */
  searchQuery: string;
  searchScope: SearchScope;
  /** Narrow the search to one documentation kind (plan 2.2), '' = off. Transient. */
  searchDocKind: SearchDocKind | '';
  /** Owner nodes of doc-content hits (plan 2.2), a debounce behind the keystrokes. */
  searchContentHits: ReadonlySet<NodeId>;
  /** Filter the map by pipeline stage (V0.38; the field keeps its historical name). */
  activeDocStatus: PipelineStage | null;
  /** Ids temporarily treated as expanded to reveal a filter hit. Derived, never stored. */
  forceOpen: ReadonlySet<NodeId>;
  /**
   * Node ids the last chatbot answer cited.
   *
   * A fourth highlight layer, on top of tag / search / pipeline-stage, and the only one that is
   * not a filter the user set: it is an answer pointing at its sources. Transient, never
   * persisted, and cleared when the dock is closed.
   */
  chatHighlight: ReadonlySet<NodeId>;
  /** The eye assignment filter's node set (V0.42) — the FIFTH highlight layer. Same
   *  content-compared mirror as chatHighlight; empty when the eye is off. */
  assignHighlight: ReadonlySet<NodeId>;

  /** Sets both fields from one raw string — the only way `searchQuery` is ever assigned. */
  setSearchText: (text: string) => void;
  setSearchScope: (scope: SearchScope) => void;
  setSearchDocKind: (kind: SearchDocKind | '') => void;
  setSearchContentHits: (ids: ReadonlySet<NodeId>) => void;
  setActiveTags: (tags: ReadonlySet<TagId>) => void;
  setActiveDocStatus: (status: PipelineStage | null) => void;
  setForceOpen: (ids: ReadonlySet<NodeId>) => void;
  setChatHighlight: (ids: ReadonlySet<NodeId>) => void;
  setAssignHighlight: (ids: ReadonlySet<NodeId>) => void;
}

/** Everything on, `content` included (plan 2.2): doc text is part of an ordinary search. */
export const DEFAULT_SEARCH_SCOPE: SearchScope = {
  name: true,
  desc: true,
  tags: true,
  content: true,
};

/** `applySearch()`'s normalisation, in the one place it happens. */
export const normaliseQuery = (text: string): string => text.trim().toLowerCase();

export const createFiltersSlice: SliceCreator<FiltersSlice> = (set, get) => ({
  activeTags: new Set<TagId>(),
  searchText: '',
  searchQuery: '',
  searchScope: DEFAULT_SEARCH_SCOPE,
  searchDocKind: '',
  searchContentHits: new Set<NodeId>(),
  activeDocStatus: null,
  forceOpen: new Set<NodeId>(),
  chatHighlight: new Set<NodeId>(),
  assignHighlight: new Set<NodeId>(),

  setSearchText: (text) => {
    const searchText = text ?? '';
    const searchQuery = normaliseQuery(searchText);
    const prev = get();
    if (prev.searchText === searchText && prev.searchQuery === searchQuery) return;
    set({ searchText, searchQuery });
  },

  setSearchScope: (scope) => {
    // Coerced field by field, like the vanilla `setSearchScope`: the scope is written from a
    // restored record and from checkbox handlers, and an absent key must read as off rather
    // than as undefined leaking into `lib/search.ts`.
    const next: SearchScope = {
      name: !!scope.name,
      desc: !!scope.desc,
      tags: !!scope.tags,
      content: !!scope.content,
    };
    // …then compared by value, because the mirror re-states it on every sync and every
    // notification costs one selector evaluation per map node. See `slices/ui.ts`.
    const prev = get().searchScope;
    if ((Object.keys(next) as (keyof SearchScope)[]).every((k) => prev[k] === next[k])) return;
    set({ searchScope: next });
  },

  setSearchDocKind: (kind) => {
    if (get().searchDocKind === kind) return;
    set({ searchDocKind: kind });
  },

  // Adopted on identity, like forceOpen: the app REPLACES the set only when the answer
  // really changed (setSearchContentHits compares by content before assigning), so identity
  // is already the signal by the time the mirror hands it over.
  setSearchContentHits: (ids) => {
    if (get().searchContentHits === ids) return;
    set({ searchContentHits: ids });
  },

  // Compared by CONTENT, like chatHighlight below: the mirror re-states the set on every
  // sync, and a restore can build an equal set with a fresh identity. Writing that through
  // would cost one selector evaluation per map node for nothing.
  setActiveTags: (tags) => {
    const prev = get().activeTags;
    if (prev === tags) return;
    if (prev.size === tags.size) {
      let same = true;
      for (const t of tags) if (!prev.has(t)) { same = false; break; }
      if (same) return;
    }
    set({ activeTags: tags });
  },

  setActiveDocStatus: (status) => {
    if (get().activeDocStatus === status) return;
    set({ activeDocStatus: status });
  },

  // Adopted, not compared: `computeForceOpen()` builds a new Set only when the reveal really
  // was recomputed, so the identity IS the signal. The mirror hands the same Set back on
  // every other sync, which this then ignores for free.
  setForceOpen: (ids) => {
    if (get().forceOpen === ids) return;
    set({ forceOpen: ids });
  },

  // Compared by CONTENT rather than adopted: the vanilla side rebuilds this set on every
  // answer, and most of those rebuilds produce the same ids (usually none at all). Adopting
  // would hand every map node a new reference and re-render all of them for nothing.
  setChatHighlight: (ids) => {
    const prev = get().chatHighlight;
    if (prev.size === ids.size) {
      let same = true;
      for (const id of ids) if (!prev.has(id)) { same = false; break; }
      if (same) return;
    }
    set({ chatHighlight: ids });
  },

  // The eye assignment layer, same content-compare as chatHighlight above.
  setAssignHighlight: (ids) => {
    const prev = get().assignHighlight;
    if (prev.size === ids.size) {
      let same = true;
      for (const id of ids) if (!prev.has(id)) { same = false; break; }
      if (same) return;
    }
    set({ assignHighlight: ids });
  },
});
